import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Stack, Group, Text, Button, Paper, TextInput, Textarea, Table, Badge,
  ActionIcon, Tooltip, CopyButton, Loader, Modal, NumberInput, Select, Alert, Menu, Progress,
} from '@mantine/core';
import {
  Plus, Copy, Check, Trash, ArrowSquareOut, Storefront, Lightning,
  ArrowsClockwise, WarningCircle, PencilSimple, Sparkle, Upload,
  DownloadSimple, MagnifyingGlass, CaretLeft, CaretRight, CaretDown, FileCsv,
} from '@phosphor-icons/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

const EMPTY_PRODUCT = {
  title: '', description: '', price: '', brand: '', gtin: '',
  product_type: '', availability: 'in_stock', condition: 'new', image_link: '',
};

const MAX_SHOPS = 5;

export default function TestShops() {
  const { t } = useTranslation();
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [activeShop, setActiveShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [merchantResult, setMerchantResult] = useState(null);
  const [pushProgress, setPushProgress] = useState(null);
  const pollingTaskRef = useRef(null);
  const [productModal, setProductModal] = useState(null); // null | {} for new | product for edit
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [productTotal, setProductTotal] = useState(0);
  const [productPage, setProductPage] = useState(1);
  const [productSearch, setProductSearch] = useState('');
  const [genModal, setGenModal] = useState(false);
  const [genCount, setGenCount] = useState(1000);
  const [merchantIdInput, setMerchantIdInput] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);
  const [error, setError] = useState('');

  // The page header is sticky (top: 56) and its height varies when the
  // description text wraps. The sidebar must pin at exactly the header's stuck
  // bottom PLUS the 16px (mb="md") gap between the header and the content row,
  // otherwise it keeps sliding up for those 16px after the header has already
  // pinned (the "sidebar moves up a bit on scroll" bug).
  const HEADER_TOP = 56;       // app top bar height
  const HEADER_GAP = 16;       // header mb="md"
  const headerRef = useRef(null);
  const [stickyTop, setStickyTop] = useState(HEADER_TOP + 64 + HEADER_GAP);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => setStickyTop(HEADER_TOP + el.offsetHeight + HEADER_GAP);
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', update);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', update); };
  }, [shops.length, loading]);

  const PER_PAGE = 50;

  const loadShops = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/shop/shops/');
      setShops(res.data.shops || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadShops(); }, []); // eslint-disable-line

  // Keep activeShop in sync with the URL (/app/test-shops/:shopId).
  useEffect(() => {
    if (!shops.length) return;
    const fromUrl = shopId && shops.find(s => s.id === shopId);
    if (fromUrl) {
      setActiveShop(fromUrl);
    } else if (!shopId) {
      // No shop in URL: select the first and reflect it in the URL.
      navigate(`/app/test-shops/${shops[0].id}`, { replace: true });
    }
  }, [shops, shopId]); // eslint-disable-line

  const loadProducts = useCallback(async (shopId, page = 1, search = '') => {
    try {
      const res = await api.get(`/shop/shops/${shopId}/products/`, { params: { page, per_page: 50, search } });
      setProducts(res.data.products || []);
      setProductTotal(res.data.total || 0);
      setProductPage(res.data.page || 1);
    } catch { setProducts([]); setProductTotal(0); }
  }, []);

  useEffect(() => {
    if (activeShop) { loadProducts(activeShop.id, 1, productSearch); setMerchantResult(null); }
  }, [activeShop?.id]); // eslint-disable-line

  const refreshActive = async () => {
    const res = await api.get(`/shop/shops/${activeShop.id}/`);
    setActiveShop(res.data);
    setShops(prev => prev.map(s => s.id === res.data.id ? res.data : s));
  };

  const createShop = async () => {
    if (shops.length >= MAX_SHOPS) {
      setError(t('testShops.error.maxShops', { max: MAX_SHOPS }));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await api.post('/shop/shops/', { name: t('testShops.newShopDefaultName') });
      setShops(prev => [res.data, ...prev]);
      navigate(`/app/test-shops/${res.data.id}`);
    } catch (e) {
      setError(e.response?.data?.error || t('testShops.error.createFailed'));
    } finally { setBusy(false); }
  };

  const saveBranding = async (patch) => {
    const res = await api.patch(`/shop/shops/${activeShop.id}/`, patch);
    setActiveShop(res.data);
    setShops(prev => prev.map(s => s.id === res.data.id ? res.data : s));
  };

  const doDeleteShop = async () => {
    await api.delete(`/shop/shops/${activeShop.id}/`);
    const remaining = shops.filter(s => s.id !== activeShop.id);
    setShops(remaining);
    setConfirmDelete(false);
    setActiveShop(remaining[0] || null);
    navigate(remaining[0] ? `/app/test-shops/${remaining[0].id}` : '/app/test-shops');
  };

  const seedDemo = async () => {
    setBusy(true);
    try {
      await api.post(`/shop/shops/${activeShop.id}/seed/`, {});
      await loadProducts(activeShop.id, 1, productSearch);
      await refreshActive();
    } finally { setBusy(false); }
  };

  const generateProducts = async () => {
    setBusy(true); setGenModal(false);
    try {
      await api.post(`/shop/shops/${activeShop.id}/generate/`, { count: Math.max(1, Math.min(Number(genCount) || 0, 50000)) });
      await loadProducts(activeShop.id, 1, productSearch);
      await refreshActive();
    } finally { setBusy(false); }
  };

  const clearProducts = async () => {
    setBusy(true); setConfirmClear(false);
    try {
      await api.delete(`/shop/shops/${activeShop.id}/products/`);
      await loadProducts(activeShop.id, 1, '');
      setProductSearch('');
      await refreshActive();
    } finally { setBusy(false); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/shop/shops/${activeShop.id}/import/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadProducts(activeShop.id, 1, productSearch);
      await refreshActive();
    } catch (err) {
      setError(err.response?.data?.error || t('testShops.error.importFailed'));
    } finally { setBusy(false); e.target.value = ''; }
  };

  const downloadTemplate = () => {
    // Example CSV (header + a few rows) the user can fill in and import back.
    // image_link uses the shop's OWN domain so images are served on-domain
    // (Merchant-crawlable). Replace with your real image URLs for real products.
    const base = (activeShop?.storefront_url || '').replace(/\/$/, '');
    const img = oid => base ? `${base}/img/${oid}.jpg` : '';
    const cols = ['offer_id', 'title', 'description', 'price', 'currency', 'brand',
      'gtin', 'condition', 'availability', 'image_link', 'product_type', 'google_product_category'];
    const tr = t('testShops.templateRows', { returnObjects: true });
    const rows = [
      ['KAWA-BRAZYLIA-1KG', tr[0].title, tr[0].description,
        '59.99', 'PLN', 'Kawiarnia Droid', '5901234123457', 'new', 'in_stock',
        img('KAWA-BRAZYLIA-1KG'), tr[0].productType, 'Food, Beverages & Tobacco > Beverages > Coffee'],
      ['MLYNEK-RECZNY-PRO', tr[1].title, tr[1].description,
        '129.00', 'PLN', 'Droid Equipment', '5901234123488', 'new', 'in_stock',
        img('MLYNEK-RECZNY-PRO'), tr[1].productType, ''],
      ['KUBEK-TERMICZNY-400', tr[2].title, tr[2].description,
        '49.00', 'PLN', 'Droid Equipment', '5901234123501', 'new', 'out_of_stock',
        img('KUBEK-TERMICZNY-400'), tr[2].productType, ''],
    ];
    const esc = v => /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v);
    const csv = [cols.join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = t('testShops.templateFilename'); a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    // Download the export (authenticated) as a blob.
    api.get(`/shop/shops/${activeShop.id}/export/`, { responseType: 'blob' }).then(res => {
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${t('testShops.exportFilenamePrefix')}${activeShop.id}.csv`; a.click();
      URL.revokeObjectURL(url);
    });
  };

  const saveProduct = async () => {
    setError('');
    try {
      if (productModal.id) {
        await api.patch(`/shop/shops/${activeShop.id}/products/${productModal.id}/`, productModal);
      } else {
        await api.post(`/shop/shops/${activeShop.id}/products/`, productModal);
      }
      setProductModal(null);
      await loadProducts(activeShop.id);
      await refreshActive();
    } catch (e) {
      setError(e.response?.data?.error ? JSON.stringify(e.response.data.error) : t('testShops.error.saveProduct'));
    }
  };

  const deleteProduct = async (pid) => {
    await api.delete(`/shop/shops/${activeShop.id}/products/${pid}/`);
    await loadProducts(activeShop.id);
    await refreshActive();
  };

  const connectMerchant = async () => {
    setBusy(true); setMerchantResult(null);
    try {
      const res = await api.post('/shop/connect-merchant/', { merchant_id: merchantIdInput.trim() });
      setMerchantIdInput('');
      await refreshActive();  // refresh so merchant_centers shows the connection
      if (res.data.note) setMerchantResult({ info: res.data.note });
    } catch (e) {
      setMerchantResult({ error: e.response?.data?.error || t('testShops.error.connectMerchant') });
    } finally { setBusy(false); }
  };

  const checkMerchant = async () => {
    setBusy(true); setMerchantResult(null);
    try {
      const res = await api.post(`/shop/shops/${activeShop.id}/merchant-check/`, {});
      setMerchantResult(res.data);
    } catch (e) {
      setMerchantResult({ error: e.response?.data?.error || t('testShops.error.checkMerchant') });
    } finally { setBusy(false); }
  };

  // Poll a running push task and drive the progress bar. Survives reloads:
  // it's started both by pushToMerchant and, on load, for any shop that has a
  // push_task_id persisted on the backend.
  const pollPushStatus = useCallback((shopId, taskId) => {
    if (!taskId || pollingTaskRef.current === taskId) return; // already polling
    pollingTaskRef.current = taskId;
    setBusy(true);
    setPushProgress(prev => prev || { phase: 'preparing', current: 0, total: 0, pushed: 0 });

    const tick = async () => {
      if (pollingTaskRef.current !== taskId) return; // superseded (shop switched)
      try {
        const st = await api.get(`/shop/shops/${shopId}/push-status/${taskId}/`);
        const d = st.data || {};
        if (d.finished || d.state === 'SUCCESS' || d.state === 'FAILURE') {
          pollingTaskRef.current = null;
          setPushProgress(null);
          setBusy(false);
          if (d.error) { setMerchantResult({ error: d.error }); return; }
          try {
            const chk = await api.post(`/shop/shops/${shopId}/merchant-check/`, {});
            setMerchantResult({ ...chk.data, pushedCount: d.pushed });
          } catch { /* ignore */ }
          return;
        }
        setPushProgress({
          phase: d.phase || 'pushing', current: d.current || 0,
          total: d.total || 0, pushed: d.pushed || 0,
          skipped: d.skipped || 0, failed: d.failed || 0,
        });
        setTimeout(tick, 1500);
      } catch {
        pollingTaskRef.current = null;
        setPushProgress(null); setBusy(false);
        setMerchantResult({ error: t('testShops.error.pushStatus') });
      }
    };
    setTimeout(tick, 600);
  }, []);

  const pushToMerchant = async () => {
    const shopId = activeShop.id;
    setBusy(true); setMerchantResult(null);
    setPushProgress({ phase: 'preparing', current: 0, total: 0, pushed: 0 });
    try {
      const res = await api.post(`/shop/shops/${shopId}/push-to-merchant/`, {});
      pollPushStatus(shopId, res.data.task_id);
    } catch (e) {
      setPushProgress(null); setBusy(false);
      setMerchantResult({ error: e.response?.data?.error || t('testShops.error.pushProducts') });
    }
  };

  // Resume the push progress bar after a reload / re-login: if the shop has a
  // running push task persisted on the backend, pick polling back up. Defined
  // here (after pollPushStatus) so the dependency reference is initialized.
  useEffect(() => {
    if (activeShop?.push_task_id) {
      pollPushStatus(activeShop.id, activeShop.push_task_id);
    } else {
      pollingTaskRef.current = null;
      setPushProgress(null);
    }
  }, [activeShop?.id, activeShop?.push_task_id, pollPushStatus]);

  if (loading) return <Group justify="center" mt="xl"><Loader color="brand" /></Group>;

  const atShopLimit = shops.length >= MAX_SHOPS;

  return (
    <div className="px-6 py-5">
      <Group ref={headerRef} justify="space-between" mb="md" align="flex-end"
        style={{ position: 'sticky', top: 56, zIndex: 20, background: '#F6F5F4',
                 marginLeft: -24, marginRight: -24, paddingLeft: 24, paddingRight: 24,
                 paddingTop: 12, paddingBottom: 12, borderBottom: '1px solid #e5e7eb' }}>
        <div>
          <Text fw={600} size="lg">{t('testShops.header.title')}</Text>
          <Text size="xs" c="dimmed">{t('testShops.header.subtitle', { max: MAX_SHOPS })}</Text>
        </div>
        <Tooltip label={t('testShops.header.limitTooltip', { max: MAX_SHOPS })} disabled={!atShopLimit} withArrow>
          <Button leftSection={<Plus size={16} />} color="brand" size="sm" onClick={createShop}
            loading={busy} disabled={atShopLimit}>
            {t('testShops.header.newShop')}
          </Button>
        </Tooltip>
      </Group>

      {error && (
        <Alert color="red" variant="light" mb="md" withCloseButton onClose={() => setError('')} p="xs">
          <Text size="xs">{error}</Text>
        </Alert>
      )}

      {shops.length === 0 ? (
        <Paper withBorder p="xl" radius="md" ta="center">
          <Storefront size={32} className="mx-auto" style={{ color: '#9ca3af' }} />
          <Text size="sm" mt="sm">{t('testShops.empty.noShops')}</Text>
          <Button mt="md" size="sm" color="brand" onClick={createShop} loading={busy}>{t('testShops.empty.createFirst')}</Button>
        </Paper>
      ) : (
        <Group align="flex-start" gap="lg" wrap="nowrap">
          {/* Shop list */}
          <Stack gap={6} style={{ width: 220, flexShrink: 0, position: 'sticky', top: stickyTop,
                                  alignSelf: 'flex-start', maxHeight: `calc(100vh - ${stickyTop + 20}px)`, overflowY: 'auto' }}>
            <Text size="xs" c="dimmed" fw={500} px={4}>{t('testShops.list.title', { count: shops.length, max: MAX_SHOPS })}</Text>
            {shops.map(s => (
              <Paper key={s.id} withBorder p="sm" radius="md"
                style={{ cursor: 'pointer', borderColor: activeShop?.id === s.id ? 'var(--mantine-color-brand-5)' : undefined,
                         background: activeShop?.id === s.id ? 'var(--mantine-color-brand-0)' : undefined }}
                onClick={() => navigate(`/app/test-shops/${s.id}`)}>
                <Text size="sm" fw={500} lineClamp={1}>{s.name}</Text>
                <Text size="xs" c="dimmed">{t('testShops.list.shopMeta', { total: s.total_products, ready: s.feed_ready_products })}</Text>
              </Paper>
            ))}
          </Stack>

          {/* Active shop detail */}
          {activeShop && (
            <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
              {/* Branding: key forces a remount on shop switch so the
                  uncontrolled (defaultValue) inputs pick up the new shop. */}
              <Paper withBorder p="md" radius="md" key={activeShop.id}>
                <Group justify="space-between" mb="xs">
                  <Text fw={600} size="sm">{t('testShops.settings.title')}</Text>
                  <Button variant="subtle" color="red" size="xs" leftSection={<Trash size={13} />} onClick={() => setConfirmDelete(true)}>{t('testShops.settings.deleteShop')}</Button>
                </Group>
                <Group grow align="flex-start">
                  <TextInput label={t('testShops.settings.nameLabel')} size="xs" defaultValue={activeShop.name}
                    onBlur={e => e.target.value !== activeShop.name && saveBranding({ name: e.target.value })} />
                  <TextInput label={t('testShops.settings.taglineLabel')} size="xs" defaultValue={activeShop.tagline}
                    onBlur={e => e.target.value !== activeShop.tagline && saveBranding({ tagline: e.target.value })} />
                </Group>
                <TextInput label={t('testShops.settings.descriptionLabel')} size="xs" mt="xs" defaultValue={activeShop.description}
                  onBlur={e => e.target.value !== activeShop.description && saveBranding({ description: e.target.value })} />
              </Paper>

              {/* Conversion tracking: paste JS tags to test conversions end to end */}
              <Paper withBorder p="md" radius="md" key={`tracking-${activeShop.id}`}>
                <Text fw={600} size="sm" mb={2}>{t('testShops.tracking.title')}</Text>
                <Text size="xs" c="dimmed" mb="sm">
                  {t('testShops.tracking.intro')} <Text span ff="monospace" size="xs">window.DROID_ORDER.value</Text> / <Text span ff="monospace" size="xs">.id</Text> / <Text span ff="monospace" size="xs">.currency</Text>.
                </Text>
                <Textarea label={t('testShops.tracking.headLabel')} size="xs" minRows={3} autosize maxRows={8}
                  placeholder={t('testShops.tracking.headPlaceholder')}
                  styles={{ input: { fontFamily: 'monospace', fontSize: 11 } }}
                  defaultValue={activeShop.tracking_head}
                  onBlur={e => e.target.value !== activeShop.tracking_head && saveBranding({ tracking_head: e.target.value })} />
                <Textarea label={t('testShops.tracking.conversionLabel')} size="xs" mt="xs" minRows={3} autosize maxRows={8}
                  placeholder={t('testShops.tracking.conversionPlaceholder')}
                  styles={{ input: { fontFamily: 'monospace', fontSize: 11 } }}
                  defaultValue={activeShop.conversion_tag}
                  onBlur={e => e.target.value !== activeShop.conversion_tag && saveBranding({ conversion_tag: e.target.value })} />
              </Paper>

              {/* Feed + storefront URLs */}
              <Paper withBorder p="md" radius="md">
                <Text fw={600} size="sm" mb="xs">{t('testShops.urls.title')}</Text>
                <UrlRow label={t('testShops.urls.feedLabel')} url={activeShop.feed_url} />
                <UrlRow label={t('testShops.urls.storefrontLabel')} url={activeShop.storefront_url} mt="xs" />
                <Alert mt="sm" color="blue" icon={<WarningCircle size={15} />} p="xs">
                  <Text size="xs">{t('testShops.urls.hint')}</Text>
                </Alert>
              </Paper>

              {/* Products */}
              <Paper withBorder p="md" radius="md">
                <Group justify="space-between" mb="sm" wrap="wrap">
                  <Text fw={600} size="sm">{t('testShops.products.title', { count: productTotal.toLocaleString('pl') })}</Text>
                  <Group gap="xs">
                    <Button variant="light" size="xs" leftSection={<Lightning size={13} />} onClick={seedDemo} loading={busy}>{t('testShops.products.seedDemo')}</Button>
                    <Button variant="light" size="xs" color="grape" leftSection={<Sparkle size={13} />} onClick={() => setGenModal(true)} loading={busy}>{t('testShops.products.generate')}</Button>
                    <Menu shadow="md" position="bottom-end" withinPortal>
                      <Menu.Target>
                        <Button variant="light" size="xs" leftSection={<Upload size={13} />} rightSection={<CaretDown size={11} />}>
                          {t('testShops.products.importCsv')}
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item component="label" leftSection={<Upload size={14} />}>
                          {t('testShops.products.importFromFile')}
                          <input type="file" accept=".csv" hidden onChange={importCsv} />
                        </Menu.Item>
                        <Menu.Item leftSection={<FileCsv size={14} />} onClick={downloadTemplate}>
                          {t('testShops.products.downloadTemplate')}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                    <Button variant="light" size="xs" leftSection={<DownloadSimple size={13} />} onClick={exportCsv} disabled={productTotal === 0}>{t('testShops.products.export')}</Button>
                    {productTotal > 0 && (
                      <Button variant="light" size="xs" color="red" leftSection={<Trash size={13} />} onClick={() => setConfirmClear(true)}>{t('testShops.products.deleteAll')}</Button>
                    )}
                    <Button size="xs" color="brand" leftSection={<Plus size={13} />} onClick={() => { setError(''); setProductModal({ ...EMPTY_PRODUCT }); }}>{t('testShops.products.add')}</Button>
                  </Group>
                </Group>
                {productTotal > 0 && (
                  <TextInput size="xs" mb="sm" placeholder={t('testShops.products.searchPlaceholder')} value={productSearch}
                    leftSection={<MagnifyingGlass size={13} />}
                    onChange={e => setProductSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && loadProducts(activeShop.id, 1, productSearch)} />
                )}
                {products.length === 0 ? (
                  <Text size="xs" c="dimmed" py="md" ta="center">{t('testShops.products.empty')}</Text>
                ) : (
                  <Table fz="xs" verticalSpacing={6} highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t('testShops.products.thProduct')}</Table.Th><Table.Th>{t('testShops.products.thPrice')}</Table.Th>
                        <Table.Th>{t('testShops.products.thAvailability')}</Table.Th><Table.Th>{t('testShops.products.thFeed')}</Table.Th><Table.Th></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {products.map(p => (
                        <Table.Tr key={p.id}>
                          <Table.Td>
                            <Text size="xs" fw={500} lineClamp={1}>{p.title}</Text>
                            <Text size="10px" c="dimmed">{p.offer_id}</Text>
                          </Table.Td>
                          <Table.Td>{p.price} {p.currency}</Table.Td>
                          <Table.Td>{p.availability === 'in_stock' ? t('testShops.availability.inStock') : p.availability === 'out_of_stock' ? t('testShops.availability.outOfStock') : t('testShops.availability.preorder')}</Table.Td>
                          <Table.Td>
                            {p.feed_ready
                              ? <Badge size="xs" color="green" variant="light">{t('testShops.products.feedReady')}</Badge>
                              : <Tooltip label={p.feed_issues.join(', ')} withArrow><Badge size="xs" color="red" variant="light">{t('testShops.products.feedIncomplete')}</Badge></Tooltip>}
                          </Table.Td>
                          <Table.Td>
                            <Group gap={2} justify="flex-end" wrap="nowrap">
                              <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => { setError(''); setProductModal({ ...p }); }}><PencilSimple size={14} /></ActionIcon>
                              <ActionIcon size="sm" variant="subtle" color="red" onClick={() => deleteProduct(p.id)}><Trash size={14} /></ActionIcon>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
                {productTotal > PER_PAGE && (
                  <Group justify="space-between" mt="sm">
                    <Text size="xs" c="dimmed">
                      {t('testShops.pagination.info', { page: productPage, total: Math.ceil(productTotal / PER_PAGE), count: productTotal.toLocaleString('pl') })}
                    </Text>
                    <Group gap={4}>
                      <ActionIcon size="sm" variant="default" disabled={productPage <= 1}
                        onClick={() => loadProducts(activeShop.id, productPage - 1, productSearch)}><CaretLeft size={14} /></ActionIcon>
                      <ActionIcon size="sm" variant="default" disabled={productPage >= Math.ceil(productTotal / PER_PAGE)}
                        onClick={() => loadProducts(activeShop.id, productPage + 1, productSearch)}><CaretRight size={14} /></ActionIcon>
                    </Group>
                  </Group>
                )}
              </Paper>

              {/* Merchant integration check */}
              <Paper withBorder p="md" radius="md">
                <Group justify="space-between" mb="xs">
                  <div>
                    <Text fw={600} size="sm">{t('testShops.merchant.title')}</Text>
                    <Text size="xs" c="dimmed">{t('testShops.merchant.subtitle')}</Text>
                  </div>
                  {(activeShop.merchant_centers || []).length > 0 && (
                    <Group gap="xs">
                      <Button size="xs" color="brand" leftSection={<Lightning size={13} />} onClick={pushToMerchant} loading={busy}>{t('testShops.merchant.pushProducts')}</Button>
                      <Button size="xs" variant="light" leftSection={<ArrowsClockwise size={13} />} onClick={checkMerchant} loading={busy}>{t('testShops.merchant.check')}</Button>
                    </Group>
                  )}
                </Group>

                {/* Connect / status */}
                {(activeShop.merchant_centers || []).length > 0 ? (
                  <Text size="xs" c="green.7" mb="xs">
                    <Check size={12} style={{ verticalAlign: 'middle' }} /> {t('testShops.merchant.connected', { list: activeShop.merchant_centers.map(m => `${m.name} (${m.merchant_id})`).join(', ') })}
                  </Text>
                ) : (
                  <Alert color="blue" p="xs" mb="xs">
                    <Text size="xs" mb={6}>{t('testShops.merchant.connectHint')}</Text>
                    <Group gap="xs" wrap="nowrap">
                      <TextInput size="xs" placeholder={t('testShops.merchant.merchantIdPlaceholder')} value={merchantIdInput}
                        onChange={e => setMerchantIdInput(e.target.value)} style={{ flex: 1 }} />
                      <Button size="xs" color="brand" onClick={connectMerchant} loading={busy} disabled={!merchantIdInput.trim()}>{t('testShops.merchant.connect')}</Button>
                    </Group>
                  </Alert>
                )}
                {pushProgress && (
                  <Alert color="blue" p="xs" mb="xs">
                    <Group justify="space-between" mb={4}>
                      <Text size="xs" fw={500}>
                        {pushProgress.phase === 'preparing'
                          ? t('testShops.merchant.pushPreparing')
                          : t('testShops.merchant.pushing', { current: pushProgress.current, total: pushProgress.total })}
                      </Text>
                      {pushProgress.phase !== 'preparing' && (
                        <Text size="xs" c="dimmed">{t('testShops.merchant.pushSent', { count: pushProgress.pushed })}{pushProgress.skipped ? t('testShops.merchant.pushSkipped', { count: pushProgress.skipped }) : ''}{pushProgress.failed ? t('testShops.merchant.pushFailed', { count: pushProgress.failed }) : ''}</Text>
                      )}
                    </Group>
                    <Progress
                      value={pushProgress.total ? (pushProgress.current / pushProgress.total) * 100 : 100}
                      animated striped size="sm" color="brand" />
                  </Alert>
                )}
                {merchantResult?.error && <Alert color="orange" p="xs"><Text size="xs">{merchantResult.error}</Text></Alert>}
                {merchantResult?.info && <Alert color="blue" p="xs" mb="xs"><Text size="xs">{merchantResult.info}</Text></Alert>}
                {merchantResult && !merchantResult.error && merchantResult.merchant && (
                  <>
                    {merchantResult.pushedCount !== undefined && (
                      <Alert color="green" p="xs" mb="xs"><Text size="xs">{t('testShops.merchant.pushedResult', { count: merchantResult.pushedCount })}</Text></Alert>
                    )}
                    {/* Summary cards */}
                    <Group gap="xs" mb="sm" grow>
                      {[
                        { label: t('testShops.merchant.cardTotal'), value: merchantResult.merchant.total, color: 'gray' },
                        { label: t('testShops.merchant.cardApproved'), value: merchantResult.merchant.approved, color: 'green' },
                        { label: t('testShops.merchant.cardPending'), value: merchantResult.merchant.pending, color: 'yellow' },
                        { label: t('testShops.merchant.cardDisapproved'), value: merchantResult.merchant.disapproved, color: 'red' },
                      ].map(s => (
                        <Paper key={s.label} withBorder p="xs" radius="sm" ta="center">
                          <Text fw={700} size="lg" c={`${s.color}.7`}>{(s.value || 0).toLocaleString('pl')}</Text>
                          <Text size="10px" c="dimmed">{s.label}</Text>
                        </Paper>
                      ))}
                    </Group>
                    <Text size="xs" c="dimmed" mb="xs">{t('testShops.merchant.productSummary', { ourProducts: merchantResult.our_products?.toLocaleString('pl'), total: merchantResult.merchant.total?.toLocaleString('pl') })}</Text>

                    {/* Problems only */}
                    {merchantResult.problems?.length > 0 ? (
                      <>
                        <Text size="xs" fw={600} c="red.7" mb={6}>{t('testShops.merchant.problemsTitle', { count: merchantResult.problems.length, suffix: merchantResult.problems_capped ? '+' : '' })}</Text>
                        <Stack gap={6}>
                          {merchantResult.problems.map(p => (
                            <Paper key={p.offer_id} withBorder radius="sm" p="xs">
                              <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs" mb={6}>
                                <div style={{ minWidth: 0 }}>
                                  <Text size="xs" fw={500} lineClamp={1}>{p.title || p.offer_id}</Text>
                                  {p.offer_id && <Text size="10px" c="dimmed" ff="monospace" lineClamp={1}>{p.offer_id}</Text>}
                                </div>
                                <Badge size="xs" color={p.status === 'disapproved' ? 'red' : 'yellow'} variant="light" style={{ flexShrink: 0 }}>
                                  {p.status === 'disapproved' ? t('testShops.merchant.statusDisapproved') : p.status === 'pending' ? t('testShops.merchant.statusPending') : p.status}
                                </Badge>
                              </Group>
                              <Stack gap={3}>
                                {(p.issues || []).map((iss, idx) => (
                                  <Group key={idx} gap={6} wrap="nowrap" align="flex-start">
                                    <WarningCircle size={13} weight="fill" color="var(--mantine-color-red-6)" style={{ marginTop: 1, flexShrink: 0 }} />
                                    <Text size="11px" c="dimmed">{iss}</Text>
                                  </Group>
                                ))}
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      </>
                    ) : (
                      <Text size="xs" c="green.7"><Check size={12} style={{ verticalAlign: 'middle' }} /> {t('testShops.merchant.noProblems')}</Text>
                    )}
                  </>
                )}
              </Paper>
            </Stack>
          )}
        </Group>
      )}

      {/* Generate products */}
      <Modal opened={genModal} onClose={() => setGenModal(false)} title={t('testShops.genModal.title')} size="sm" centered>
        <Text size="xs" c="dimmed" mb="sm">
          {t('testShops.genModal.intro')}
        </Text>
        <NumberInput label={t('testShops.genModal.countLabel')} size="xs" min={1} max={50000} step={1000}
          value={genCount} onChange={setGenCount} thousandSeparator=" " />
        <Group gap={6} mt="xs">
          {[100, 1000, 5000, 25000].map(n => (
            <Button key={n} size="compact-xs" variant="light" onClick={() => setGenCount(n)}>{n.toLocaleString('pl')}</Button>
          ))}
        </Group>
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" color="gray" size="xs" onClick={() => setGenModal(false)}>{t('testShops.common.cancel')}</Button>
          <Button color="grape" size="xs" leftSection={<Sparkle size={13} />} onClick={generateProducts} loading={busy}>{t('testShops.products.generate')}</Button>
        </Group>
      </Modal>

      {/* Clear all products confirmation */}
      <Modal opened={confirmClear} onClose={() => setConfirmClear(false)} title={t('testShops.clearModal.title')} size="sm" centered>
        <Text size="sm" mb="md">
          {t('testShops.clearModal.prefix')} <b>{t('testShops.clearModal.allProducts', { count: productTotal.toLocaleString('pl') })}</b> {t('testShops.clearModal.fromShop')}
          <b> {activeShop?.name}</b>{t('testShops.clearModal.suffix')}
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" color="gray" size="xs" onClick={() => setConfirmClear(false)}>{t('testShops.common.cancel')}</Button>
          <Button color="red" size="xs" leftSection={<Trash size={13} />} onClick={clearProducts} loading={busy}>{t('testShops.products.deleteAll')}</Button>
        </Group>
      </Modal>

      {/* Delete shop confirmation */}
      <Modal opened={confirmDelete} onClose={() => setConfirmDelete(false)} title={t('testShops.deleteModal.title')} size="sm" centered>
        <Text size="sm" mb="md">
          {t('testShops.deleteModal.prefix')} <b>{activeShop?.name}</b>{t('testShops.deleteModal.suffix')}
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" color="gray" size="xs" onClick={() => setConfirmDelete(false)}>{t('testShops.common.cancel')}</Button>
          <Button color="red" size="xs" leftSection={<Trash size={13} />} onClick={doDeleteShop}>{t('testShops.settings.deleteShop')}</Button>
        </Group>
      </Modal>

      {/* Product editor modal */}
      <Modal opened={!!productModal} onClose={() => setProductModal(null)} title={productModal?.id ? t('testShops.productModal.editTitle') : t('testShops.productModal.newTitle')} size="md" centered>
        {productModal && (
          <Stack gap="xs">
            {error && <Alert color="red" p="xs"><Text size="xs">{error}</Text></Alert>}
            <TextInput label={t('testShops.productModal.titleLabel')} size="xs" value={productModal.title} onChange={e => setProductModal({ ...productModal, title: e.target.value })} />
            <Textarea label={t('testShops.productModal.descriptionLabel')} size="xs" minRows={2} value={productModal.description} onChange={e => setProductModal({ ...productModal, description: e.target.value })} />
            <Group grow>
              <NumberInput label={t('testShops.productModal.priceLabel')} size="xs" decimalScale={2} value={productModal.price} onChange={v => setProductModal({ ...productModal, price: v })} />
              <TextInput label={t('testShops.productModal.brandLabel')} size="xs" value={productModal.brand} onChange={e => setProductModal({ ...productModal, brand: e.target.value })} />
            </Group>
            <Group grow>
              <TextInput label={t('testShops.productModal.gtinLabel')} size="xs" value={productModal.gtin} onChange={e => setProductModal({ ...productModal, gtin: e.target.value })} />
              <Select label={t('testShops.productModal.availabilityLabel')} size="xs" value={productModal.availability}
                onChange={v => setProductModal({ ...productModal, availability: v })}
                data={[{ value: 'in_stock', label: t('testShops.availability.inStock') }, { value: 'out_of_stock', label: t('testShops.availability.outOfStock') }, { value: 'preorder', label: t('testShops.availability.preorder') }]} />
            </Group>
            <TextInput label={t('testShops.productModal.categoryLabel')} size="xs" value={productModal.product_type} onChange={e => setProductModal({ ...productModal, product_type: e.target.value })} />
            <TextInput label={t('testShops.productModal.imageLabel')} size="xs" value={productModal.image_link} onChange={e => setProductModal({ ...productModal, image_link: e.target.value })} />
            <Group justify="flex-end" mt="xs">
              <Button variant="subtle" color="gray" size="xs" onClick={() => setProductModal(null)}>{t('testShops.common.cancel')}</Button>
              <Button size="xs" color="brand" onClick={saveProduct}>{t('testShops.common.save')}</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
}

function UrlRow({ label, url, mt }) {
  const { t } = useTranslation();
  return (
    <div style={{ marginTop: mt ? 8 : 0 }}>
      <Text size="10px" c="dimmed" mb={2}>{label}</Text>
      <Group gap="xs" wrap="nowrap">
        <Text size="xs" style={{ flex: 1, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</Text>
        <CopyButton value={url}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? t('testShops.urlRow.copied') : t('testShops.urlRow.copy')} withArrow>
              <ActionIcon size="sm" variant="subtle" color={copied ? 'green' : 'gray'} onClick={copy}>{copied ? <Check size={14} /> : <Copy size={14} />}</ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
        <Tooltip label={t('testShops.urlRow.open')} withArrow>
          <ActionIcon size="sm" variant="subtle" color="gray" component="a" href={url} target="_blank"><ArrowSquareOut size={14} /></ActionIcon>
        </Tooltip>
      </Group>
    </div>
  );
}
